/* ═══════════════════════════════════════════════════════════════
   WidgetFrame — the one chrome every dashboard widget body renders
   in: a compact title row (+ meta, "Always on" mark for required
   widgets, a right-aligned actions slot, and a header overflow menu)
   over a status body that handles loading / empty / error / ready
   without every widget re-inventing its own skeleton or EmptyState
   wiring, plus an optional quiet footer link row ("View all N").
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, MoreHorizontal, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { rem } from '../../lib/rem';
import { Icon } from '../ui/Icon';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent } from '../ui/context-menu';
import { toneToVar, type ChartTone } from './WChart';
import EmptyState from '../EmptyState';

export type WidgetStatus = 'loading' | 'empty' | 'error' | 'ready';

/** Selects the skeleton shape rendered while `status === 'loading'`. */
export type WidgetForm = 'stat' | 'meter' | 'list' | 'chart' | 'stepper' | 'fields';

export interface WidgetEmptyConfig {
  title: string;
  subtitle?: string;
  action?: { label: string; onClick: () => void };
}

export interface WidgetErrorConfig {
  message: string;
  onRetry?: () => void;
}

export interface WidgetFooterConfig {
  label: string;
  onClick: () => void;
}

export interface WidgetFrameProps {
  title: ReactNode;
  /** Builder R2 — a user-built widget's picked icon, rendered before the
   *  title the same way a stock widget would if it ever grows one (no
   *  stock widget carries an icon today; this is additive). */
  icon?: LucideIcon;
  /** Tone for `icon` — a status/series identity, never a raw color. */
  iconTone?: ChartTone;
  /** Marks a widget that can't be hidden from the dashboard. */
  required?: boolean;
  status: WidgetStatus;
  /** Right-of-title control slot — pencil / Manage / expand, supplied by
   *  the page (WidgetFrame itself stays generic, no page imports). */
  actions?: ReactNode;
  /** Count / one-line subtitle rendered beside the title, muted micro-meta. */
  meta?: ReactNode;
  /** Header overflow menu content (ContextMenuItem elements) — rendered
   *  behind an icon-only ⋯ trigger built from the kit's context-menu. */
  menu?: ReactNode;
  /** Quiet link row under the body, e.g. `{ label: 'View all 27', onClick }`. */
  footer?: WidgetFooterConfig;
  /** Skeleton shape shown while loading. Defaults to a generic 3-line skeleton. */
  form?: WidgetForm;
  empty?: WidgetEmptyConfig;
  error?: WidgetErrorConfig;
  children?: ReactNode;
}

const SKELETON_WIDTHS = ['w-full', 'w-5/6', 'w-2/3'] as const;

function DefaultSkeleton() {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading">
      {SKELETON_WIDTHS.map((w) => (
        <div key={w} className={cn('h-4 rounded-md bg-surface-alt motion-safe:animate-pulse', w)} />
      ))}
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading">
      <div
        className="rounded-md bg-surface-alt motion-safe:animate-pulse"
        style={{ height: rem(32), width: rem(96) }}
      />
      <div className="h-3 w-1/3 rounded-md bg-surface-alt motion-safe:animate-pulse" />
    </div>
  );
}

function MeterSkeleton() {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading">
      <div className="h-3 w-1/4 rounded-md bg-surface-alt motion-safe:animate-pulse" />
      <div
        className="rounded-md bg-surface-alt motion-safe:animate-pulse"
        style={{ height: rem(8) }}
      />
      <div className="flex justify-between gap-2">
        <div className="h-3 w-1/5 rounded-md bg-surface-alt motion-safe:animate-pulse" />
        <div className="h-3 w-1/5 rounded-md bg-surface-alt motion-safe:animate-pulse" />
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-2" style={{ minHeight: rem(32) }}>
          <div className="h-6 w-6 flex-shrink-0 rounded-full bg-surface-alt motion-safe:animate-pulse" />
          <div
            className={cn(
              'h-3 flex-1 rounded-md bg-surface-alt motion-safe:animate-pulse',
              i % 2 ? 'w-2/3' : 'w-full'
            )}
          />
        </div>
      ))}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div
      className="w-full flex-1 rounded-md bg-surface-alt motion-safe:animate-pulse"
      style={{ minHeight: rem(80) }}
      role="status"
      aria-label="Loading"
    />
  );
}

function StepperSkeleton() {
  return (
    <div className="flex items-center gap-2" role="status" aria-label="Loading">
      {[0, 1, 2, 3].map((i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="h-px flex-1 bg-surface-alt motion-safe:animate-pulse" />}
          <div
            className="flex-shrink-0 rounded-full bg-surface-alt motion-safe:animate-pulse"
            style={{ height: rem(24), width: rem(24) }}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

function FieldsSkeleton() {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label="Loading">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-1">
          <div className="h-2 w-1/5 rounded-md bg-surface-alt motion-safe:animate-pulse" />
          <div className="h-3 w-2/3 rounded-md bg-surface-alt motion-safe:animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function FormSkeleton({ form }: { form?: WidgetForm }) {
  switch (form) {
    case 'stat':
      return <StatSkeleton />;
    case 'meter':
      return <MeterSkeleton />;
    case 'list':
      return <ListSkeleton />;
    case 'chart':
      return <ChartSkeleton />;
    case 'stepper':
      return <StepperSkeleton />;
    case 'fields':
      return <FieldsSkeleton />;
    default:
      return <DefaultSkeleton />;
  }
}

export function WidgetFrame({
  title,
  icon,
  iconTone,
  required,
  status,
  actions,
  meta,
  menu,
  footer,
  form,
  empty,
  error,
  children,
}: WidgetFrameProps) {
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {/* The card's ONE header. `wg-drag-handle` makes it the drag grip in
          the dashboard's Customize mode (inert anywhere else). */}
      <div className="wg-drag-handle flex flex-shrink-0 items-center gap-2">
        {icon && (
          <Icon
            icon={icon}
            size="sm"
            className="flex-shrink-0"
            style={{ color: toneToVar(iconTone) }}
          />
        )}
        <span className="flex-1 truncate text-sm font-semibold text-text">{title}</span>
        {meta && <span className="flex-shrink-0 truncate text-xs text-muted">{meta}</span>}
        {required && (
          <span
            title="Always on — this widget can't be hidden from the dashboard"
            className="rounded-full border border-border px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-subtle"
          >
            Always on
          </span>
        )}
        {actions && <div className="flex flex-shrink-0 items-center gap-1">{actions}</div>}
        {menu && (
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                title="More actions"
                className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-transparent text-muted transition-colors hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Icon icon={MoreHorizontal} size="sm" />
              </button>
            </ContextMenuTrigger>
            <ContextMenuContent
              align="end"
              sideOffset={4}
              className="min-w-[10rem] rounded-md border border-border bg-surface p-1 shadow-md"
            >
              {menu}
            </ContextMenuContent>
          </ContextMenu>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {status === 'loading' && <FormSkeleton form={form} />}
        {(status === 'error' || (status === 'empty' && empty)) && (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            {status === 'error' ? (
              <EmptyState
                compact
                icon={<Icon icon={AlertTriangle} em={1.75} className="text-danger" />}
                title={error?.message ?? 'Something went wrong'}
                action={error?.onRetry ? { label: 'Try again', onClick: error.onRetry } : undefined}
              />
            ) : (
              <EmptyState
                compact
                title={empty!.title}
                subtitle={empty!.subtitle}
                action={empty!.action}
              />
            )}
          </div>
        )}
        {status === 'ready' && children}
      </div>
      {status === 'ready' && footer && (
        <button
          type="button"
          onClick={footer.onClick}
          className="flex-shrink-0 self-start text-xs font-semibold text-muted transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          style={{ minHeight: rem(24) }}
        >
          {footer.label}
        </button>
      )}
    </div>
  );
}

export interface WidgetActionButtonProps {
  icon: React.ComponentProps<typeof Icon>['icon'];
  label: string;
  onClick: () => void;
  active?: boolean;
}

/** Icon-only header action (pencil / Manage / expand). ≥2rem hit area. */
export function WidgetActionButton({ icon, label, onClick, active }: WidgetActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-muted transition-colors hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        active && 'bg-hover text-text'
      )}
    >
      <Icon icon={icon} size="sm" />
    </button>
  );
}
